import axios from 'axios';
import { getAuthHeader } from './authApi';

export interface TopGoals {
    id: string;
    name: string;
    logo_url: string;
    goals: number;
    user_bet: boolean;
}

export interface MatchMostGoals {
    match: string;
    score: string;
    total_goals: number;
    user_bet: boolean;
}

export interface OverallTeamStandings {
    team_id: number;
    team_name: string;
    logo_url: string;
    matches_played: number;
    wins: number;
    draws: number;
    losses: number;
    goals_for: number;
    goals_against: number;
    goal_difference: number;
    points: number;
  }

  export interface FairPlayStandings {
    team_id: number;
    team_name: string;
    logo_url: string;
    yellow_cards: number;
    second_yellow_cards: number;
    red_cards: number;
    fair_play_points: number;
  }
export interface TournamentStatsResponse {
  top_goal_scorers: TopGoals[];
  team_most_goals: TopGoals[];
  match_most_goals: MatchMostGoals[];
  best_team_standings: OverallTeamStandings[];
  worst_team_standings: OverallTeamStandings[];
  third_place_standings: OverallTeamStandings[];
  fair_play_standings: FairPlayStandings[];
  general_bets_summary: GeneralBetSummary[];
}

export interface GeneralBetSummary {
  bet_id: number;
  bet_name: string;
  bet_type: string;
  option_label: string;
  user_count: number;
  user_bet: boolean;
}

/**
 * Fetches top goal scorers from the API
 * @returns Array of top goal scorers with user bet information
 */
export const getTopGoalScorers = async (): Promise<TopGoals[]> => {
  try {
    const response = await axios.get<TournamentStatsResponse>('/api/tournament-stats/top-goal-scorers', {
      headers: getAuthHeader()
    });
    return response.data.top_goal_scorers || [];
  } catch (error) {
    console.error('Error fetching top goal scorers:', error);
    return [];
  }
};

/**
 * Fetches team goals count from the API
 * @returns Array of teams with goals count with user bet information
 */
export const getTeamGoals = async (): Promise<TopGoals[]> => {
    try {
        const response = await axios.get<TournamentStatsResponse>('/api/tournament-stats/team-most-goals', {
        headers: getAuthHeader()
        });
        return response.data.team_most_goals || [];
    } catch (error) {
        console.error('Error fetching top goal scorers:', error);
        return [];
    }
};

/**
 * Fetches match with the mose goals count from the API
 * @returns Array of matches with goals count with user bet information
 */
export const getMatchMostGoals = async (): Promise<MatchMostGoals[]> => {
    try {
        const response = await axios.get<TournamentStatsResponse>('/api/tournament-stats/match-most-goals', {
        headers: getAuthHeader()
        });
        return response.data.match_most_goals || [];
    } catch (error) {
        console.error('Error fetching match with most goals:', error);
        return [];
    }
};

export const getBestTeamStandings = async (): Promise<OverallTeamStandings[]> => {
    try {
        const response = await axios.get<TournamentStatsResponse>('/api/tournament-stats/best-team-standings', {
        headers: getAuthHeader()
        });
        return response.data.best_team_standings || [];
    } catch (error) {
        console.error('Error fetching best team standings:', error);
        return [];
    }
};

export const getWorstTeamStandings = async (): Promise<OverallTeamStandings[]> => {
    try {
        const response = await axios.get<TournamentStatsResponse>('/api/tournament-stats/worst-team-standings', {
        headers: getAuthHeader()
        });
        return response.data.worst_team_standings || [];
    } catch (error) {
        console.error('Error fetching worst team standings:', error);
        return [];
    }
};

export const getThirdPlaceStandings = async (): Promise<OverallTeamStandings[]> => {
    try {
        const response = await axios.get<TournamentStatsResponse>('/api/tournament-stats/third-place-standings', {
        headers: getAuthHeader()
        });
        return response.data.third_place_standings || [];
    } catch (error) {
        console.error('Error fetching third place standings:', error);
        return [];
    }
};

export const getFairPlayStandings = async (): Promise<FairPlayStandings[]> => {
    try {
        const response = await axios.get<TournamentStatsResponse>('/api/tournament-stats/fair-play-standings', {
        headers: getAuthHeader()
        });
        return response.data.fair_play_standings || [];
    } catch (error) {
        console.error('Error fetching fair play standings:', error);
        return [];
    }
};

export const getRankedTeamStandings = async (): Promise<OverallTeamStandings[]> => {
    try {
        const response = await axios.get<TournamentStatsResponse>('/api/tournament-stats/ranked-team-standings', {
        headers: getAuthHeader()
        });
        return (response.data as any).ranked_team_standings || [];
    } catch (error) {
        console.error('Error fetching ranked team standings:', error);
        return [];
    }
};

export const getGeneralBetsSummary = async (): Promise<GeneralBetSummary[]> => {
  try {
    const response = await axios.get<TournamentStatsResponse>('/api/tournament-stats/general-bets-summary', {
      headers: getAuthHeader()
    });
    return response.data.general_bets_summary || [];
  } catch (error) {
    console.error('Error fetching general bets summary:', error);
    return [];
  }
};

